// Drop a file (after Bencho's upload dropzone). Drag a file over the zone and it lights up and leans in; drop it (or
// pick it) and it's checked on the spot: a file of the wrong kind or too big is refused with the reason, and the zone
// gives a little shake. Self-registers `uiDrop` on alpine:init; x-ui.dropzone renders it.
//
//   uiDrop({ accept: ['audio/', 'video/'], maxMb: null, kind: 'an audio file', links: false, take: 'chooseFile' })
// `take` names the method in scope that gets { target: { files } }, like a file input's change event. With
// `links: true` the zone wraps a textarea instead: a dropped CSV or text file's links and @handles are added to it,
// one per line, skipping ones already there.

const LINK = /(https?:\/\/[^\s,;"'<>]+|(?:^|[\s,;"'])@[A-Za-z0-9._]{2,30})/g;

export function uiDrop(opts = {}) {
    return {
        over: false,
        bad: '',
        note: '',
        depth: 0,
        enter(e) {
            if (![...(e.dataTransfer?.types ?? [])].includes('Files')) return;
            this.depth++;
            this.over = true;
        },
        leave() { if (--this.depth <= 0) { this.depth = 0; this.over = false; } },
        drop(e) {
            this.depth = 0;
            this.over = false;
            const f = e.dataTransfer?.files?.[0];
            if (f) this.take(f);
        },
        pick(e) { const f = e.target.files?.[0]; if (f) this.take(f, e); },
        refuse(why) {
            this.bad = why;
            this.note = '';
            const z = this.$refs.zone;
            z?.classList.remove('ui-drop-shake');
            void z?.offsetWidth;
            z?.classList.add('ui-drop-shake');
        },
        take(f, event = null) {
            this.bad = '';
            const type = f.type || '';
            const ext = (f.name.split('.').pop() || '').toLowerCase();
            if (opts.links) {
                if (!(type.startsWith('text/') || ['csv', 'txt', 'tsv'].includes(ext))) {
                    return this.refuse(`${f.name} isn't a CSV or text file. Export the list as CSV and drop that.`);
                }
            } else if (opts.accept?.length && !opts.accept.some((a) => type.startsWith(a))) {
                return this.refuse(`${f.name} isn't ${opts.kind ?? 'a file we can open'}.`);
            }
            if (opts.maxMb && f.size > opts.maxMb * 1e6) {
                return this.refuse(`${f.name} is ${(f.size / 1e6).toFixed(f.size >= 1e7 ? 0 : 1)} MB. The limit is ${opts.maxMb} MB.`);
            }
            if (!opts.links) {
                const fn = this[opts.take ?? 'chooseFile'];
                fn?.call(this, event ?? { target: { files: [f] } });
                return;
            }
            f.text().then((text) => {
                const ta = this.$root.querySelector('textarea');
                if (!ta) return;
                const have = new Set(ta.value.split(/\s+/).filter(Boolean).map((v) => v.toLowerCase()));
                const found = [...text.matchAll(LINK)].map((m) => m[1].trim().replace(/^[,;"']/, '').replace(/[.,;)]+$/, ''));
                const fresh = [...new Set(found)].filter((v) => !have.has(v.toLowerCase()));
                if (!found.length) return this.refuse(`We didn't find any links or @usernames in ${f.name}.`);
                ta.value = [ta.value.trim(), ...fresh].filter(Boolean).join('\n');
                ['input', 'change', 'blur'].forEach((t) => ta.dispatchEvent(new Event(t, { bubbles: true })));
                this.note = fresh.length
                    ? `Added ${fresh.length} from ${f.name}${found.length > fresh.length ? ` (${found.length - fresh.length} already in the list)` : ''}.`
                    : `Everything in ${f.name} is already in the list.`;
            });
        },
    };
}

if (typeof window !== 'undefined' && !window.__uiDrop) {
    window.__uiDrop = true;
    document.addEventListener('alpine:init', () => window.Alpine.data('uiDrop', uiDrop));
}
